// Bank Deposit verification (legacy /AccountsApproval/VerifyBankDeposit) — cost center cash
// deposited into a bank. Pending rows are verified/approved via the shared status list (legacy
// drops "Return" from this page's dropdown); rows returned for update (TranStatus '0') get legacy's
// resubmit form (bank, date, amount, description editable; amount capped at the CC's cash).
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Banknote, Clock, Building2, RotateCcw, Send } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchVerifyDepositQueue,
    fetchVerifyDepositDetail,
    fetchReturnedDepositDetail,
    fetchDepositBanks,
    submitVerifyDeposit,
    submitUpdateDeposit,
    clearVerifyDepositDetail,
    resetVerifyBankDeposit,
    selectVBDQueue, selectVBDQueueLoading, selectVBDQueueError,
    selectVBDDetail, selectVBDDetailLoading, selectVBDDetailError,
    selectVBDBanks, selectVBDVerifyLoading, selectVBDUpdateLoading,
} from '../../slices/accountsSlice/verifyBankDepositSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Reject: 'Rejected Successfully',
};

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const formatAmount = (v) => (v != null && v !== '' ? `₹ ${formatIndianCurrency(v)}` : null);
const todayIso = () => new Date().toISOString().slice(0, 10);
// "2026-09-25T00:00:00" → "2026-09-25" for <input type="date">
const toIsoDate = (v) => (v ? String(v).slice(0, 10) : '');

const VerifyBankDeposit = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVBDQueue);
    const queueLoading = useSelector(selectVBDQueueLoading);
    const queueError = useSelector(selectVBDQueueError);
    const detail = useSelector(selectVBDDetail);
    const detailLoading = useSelector(selectVBDDetailLoading);
    const detailError = useSelector(selectVBDDetailError);
    const banks = useSelector(selectVBDBanks);
    const verifyLoading = useSelector(selectVBDVerifyLoading);
    const updateLoading = useSelector(selectVBDUpdateLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    // Returned-row resubmit form
    const [updBankId, setUpdBankId] = useState('');
    const [updDate, setUpdDate] = useState('');
    const [updAmount, setUpdAmount] = useState('');
    const [updDescription, setUpdDescription] = useState('');

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.TranStatus) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const deposit = selectedItem && detail?.Id === selectedItem.Id ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyDepositQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy removes "Return" from this page's status dropdown.
        dispatch(setShowReturnButton('No'));
        return () => {
            dispatch(resetVerifyBankDeposit());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyDepositDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('No'));
        if (String(selectedItem.TranStatus) === '0') {
            dispatch(fetchReturnedDepositDetail(selectedItem.Id));
            if (!banks.length) dispatch(fetchDepositBanks());
        } else {
            dispatch(fetchVerifyDepositDetail(selectedItem.Id));
        }
        setComment('');
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // The queue row carries no MOID; the pending-detail row does.
    useEffect(() => {
        if (isReturned || !deposit?.MOID || !roleId) return;
        dispatch(fetchStatusList({ MOID: deposit.MOID, ROID: roleId, ChkAmt: 0 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deposit?.MOID, deposit?.Id]);

    // Prefill the resubmit form once the returned deposit (and bank list) are in.
    useEffect(() => {
        if (!isReturned || !deposit) return;
        const bank = banks.find((b) => b.Bank_Name === deposit.Transfer_Bank);
        setUpdBankId(bank ? String(bank.Bank_Id) : '');
        setUpdDate(toIsoDate(deposit.Transfer_Date));
        setUpdAmount(deposit.Transfer_Amount != null ? String(deposit.Transfer_Amount) : '');
        setUpdDescription(deposit.Description || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deposit?.Id, banks.length]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyDepositQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyDepositQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyDepositDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!deposit) { toast.error('No deposit selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        const payload = {
            Id: deposit.Id,
            Approvalstatus: actionValue,
            ApprovalRemarks: comment.trim(),
            Createdby: getCurrentUser(),
            RoleID: roleId,
        };

        try {
            const result = await dispatch(submitVerifyDeposit(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Bank Deposit ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || `${actionValue} was not applied — the server returned no confirmation.`);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit deposit verification');
        }
    };

    const ccAvailable = parseFloat(deposit?.CCAmount) || 0;

    // Legacy clears the amount when it exceeds the cost center's cash (CCAmount already adds back
    // this deposit's own amount, so the original figure is always allowed).
    const handleAmountChange = (e) => {
        const value = e.target.value;
        if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;
        if (value !== '' && (ccAvailable === 0 || parseFloat(value) > ccAvailable)) {
            toast.error('Invalid Attempt — amount exceeds the cost center cash available');
            setUpdAmount('');
            return;
        }
        setUpdAmount(value);
    };

    const handleResubmit = async () => {
        if (!deposit) return;
        const errors = [];
        if (!updBankId) errors.push('Select Bank Name');
        if (!updDate) errors.push('Select Date');
        if (!updDescription.trim()) errors.push('Enter Description');
        if (!updAmount || parseFloat(updAmount) <= 0) errors.push('Enter Amount');
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        const payload = {
            Id: deposit.Id,
            CC_Amount: deposit.CCAmount,
            Transfer_Cost_Center: deposit.Transfer_Cost_Center,
            Transfer_Bank: updBankId,
            Transfer_Date: updDate,
            Transfer_Amount: parseFloat(updAmount),
            Description: updDescription.trim(),
            Createdby: getCurrentUser(),
            RoleID: roleId,
        };

        try {
            const result = await dispatch(submitUpdateDeposit(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            // spUpdateDeposit's own success literal is "Successfull".
            if (status === 'Successfull' || status === 'Submitted') {
                toast.success('Bank Deposit Updated Successfully');
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Error Occurred While Return Deposit Updation');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error Occurred While Return Deposit Updation');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.Bank?.toLowerCase().includes(q)
            || item.TransferCostCenter?.toLowerCase().includes(q)
            || item.Date?.toLowerCase().includes(q)
            || String(item.Amount ?? '').includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Banknote className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.TransferCostCenter}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.Date}</p>
                </div>
                <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{formatAmount(item.Amount)}</span>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1 min-w-0"><Building2 className="w-3 h-3 flex-shrink-0" /><span className="truncate">{item.Bank}</span></span>
                    <StatusBadge status={item.TranStatus} />
                </div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="text-gray-500 dark:text-gray-400 min-w-[100px]">{item.Date}</span>
            <span className="font-semibold text-gray-900 dark:text-white min-w-[160px] truncate">{item.TransferCostCenter}</span>
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[260px]">{item.Bank}</span>
            <span className="font-semibold text-gray-900 dark:text-white">{formatAmount(item.Amount)}</span>
            <span className="ml-auto"><StatusBadge status={item.TranStatus} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Banknote className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderPendingDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Date" value={deposit.Date} />
                <Field label="Bank Name" value={deposit.Bank} />
                <Field label="Cost / Job Center" value={deposit.TransferCostCenter} />
                <Field label="Amount" value={formatAmount(deposit.Amount)} />
                <Field label="Remarks" value={deposit.Remarks} className="sm:col-span-2 lg:col-span-4" />
            </div>
        </div>
    );

    const renderResubmitForm = () => (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 space-y-4">
            <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for Update — correct and resubmit</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Cost / Job Center" value={deposit.Transfer_Cost_Center} />
                <Field label="Cost Center Cash Available" value={formatAmount(deposit.CCAmount)} />
                <div>
                    <label className={labelCls}>Bank Name <span className="text-red-500">*</span></label>
                    <select className={inputCls} value={updBankId} onChange={(e) => setUpdBankId(e.target.value)}>
                        <option value="">-Please Select-</option>
                        {banks.map((b) => <option key={b.Bank_Id} value={b.Bank_Id}>{b.Bank_Name}</option>)}
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Date <span className="text-red-500">*</span></label>
                    <input type="date" className={inputCls} value={updDate} max={todayIso()} onChange={(e) => setUpdDate(e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>Amount <span className="text-red-500">*</span></label>
                    <input type="text" inputMode="decimal" className={inputCls} value={updAmount} onChange={handleAmountChange} />
                </div>
                <div className="sm:col-span-2">
                    <label className={labelCls}>Description <span className="text-red-500">*</span></label>
                    <textarea rows={3} className={inputCls} value={updDescription} onChange={(e) => setUpdDescription(e.target.value)} />
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={updateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {updateLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                    Resubmit
                </button>
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this deposit</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!deposit && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading deposit details…</span>
                </div>
            );
        }
        if (detailError || !deposit) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Deposit details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{selectedItem.TransferCostCenter}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{formatAmount(selectedItem.Amount)}</p>
                    <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">{selectedItem.Bank}</span>
                        <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Date: {selectedItem.Date}</span>
                        <StatusBadge status={selectedItem.TranStatus} />
                    </div>
                </div>

                {isReturned ? renderResubmitForm() : (
                    <>
                        {renderPendingDetails()}
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Bank Deposit Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Banknote}
                badgeText="Bank Deposit Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by cost center, bank, date, amount…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No bank deposits pending verification!',
                        itemKey: 'Id',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Bank Deposit Verification',
                        icon: Banknote,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Deposit Details',
                        icon: Banknote,
                        selectedTitle: 'Bank Deposit Verification',
                        emptyTitle: 'No Deposit Selected',
                        emptyMessage: 'Select a deposit from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyBankDeposit;
